import { uniqueId } from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import { Checkbox, HeaderCell } from 'src/components/atoms';
import { BodyText } from 'src/components/atomsLegacy';
import { EmptyTableView } from 'src/components/moleculesLegacy/CaseDocumentsTable/CaseDocumentsTable.styles';
import { ColumnType, TableRow } from '../TableRow';
import {
  EmptyTableStateBorderless,
  HeaderRowStyled,
  TableBody,
  TableHead,
  TableStyled,
} from './Table.styles';

export type OnRowClickType<Data> = (
  row: string,
  data: Data | undefined,
  e: React.MouseEvent,
) => void;

interface Props<Data> {
  columns: ColumnType[];
  sortableColumns?: string[];
  columnInfoTooltips?: { columnLabel: string; tooltip: string }[];
  tableData: {
    cellData: (ReactNode | string | number)[];
    row: string;
    data?: Data;
  }[];
  allowRowSelect?: boolean;
  selectedRows?: string[];
  customEmptyState?: React.JSX.Element;
  onRowClick?: OnRowClickType<Data>;
  onColumnSort?: (columnName: string, direction: 'asc' | 'desc') => void;
  sortedColumn?: { columnName: string; direction: 'asc' | 'desc' };
  'data-testid'?: string;
  fixedRowHeight?: boolean;
  isScrollable?: boolean;
  isVerticalScrollOnly?: boolean;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  small?: boolean;
  rowHeight?: number;
  maxRows?: number;
  borderRadius?: string;
  visibleColumns?: ColumnType[];
  emptyText: string;
  selectAll?: () => void;
  allSelected?: boolean;
  maxColumnWidth?: string;
  hasStickyHeader?: boolean;
  columnsWidths?: (string | undefined)[];
  emptyStateLayout?: 'default' | 'borderless';
  selectionVariant?: 'checkbox' | 'radio';
  selectionGroupName?: string;
}

export const Table = <Data,>({
  isFullHeight = false,
  columns,
  visibleColumns = columns,
  hasStickyHeader = true,
  'data-testid': testId,
  ...props
}: Props<Data>) => {
  const tableRef = React.useRef<HTMLTableElement>(null);
  const isHorizontalScrollAtZero = () => tableRef.current?.scrollLeft === 0;
  const [isScrolled, setIsScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(isHorizontalScrollAtZero());
    tableRef.current?.addEventListener('scroll', handleScroll);
    const ref = tableRef.current;
    return () => ref?.removeEventListener('scroll', handleScroll);
  }, []);

  const renderEmptyState = (component: React.JSX.Element | undefined) => {
    const EmptyStateShell =
      props.emptyStateLayout === 'borderless'
        ? EmptyTableStateBorderless
        : EmptyTableView;
    return (
      <tr data-testid={`${testId}-empty`} key={`${testId}-empty`}>
        <td
          colSpan={props.allowRowSelect ? columns.length + 1 : columns.length}
          height={
            props.small
              ? '150px'
              : props.maxRows && props.rowHeight
                ? props.maxRows * props.rowHeight
                : '578px'
          }
        >
          <EmptyStateShell data-testid={`${testId}-empty-container`}>
            {component || (
              <>
                <img
                  src="/assets/addFileGrey.svg"
                  alt="Add file icon"
                  width="92px"
                  height="101px"
                />
                <BodyText weight="regular">{props.emptyText}</BodyText>
              </>
            )}
          </EmptyStateShell>
        </td>
      </tr>
    );
  };

  return (
    <TableStyled
      data-testid={testId}
      ref={tableRef}
      $isScrollable={!!props.isScrollable}
      $isScrolled={isScrolled}
      $maxRows={props.maxRows}
      $rowHeight={props.rowHeight}
      $isFullWidth={props.isFullWidth}
      $isVerticalScrollOnly={props.isVerticalScrollOnly}
      $isFullHeight={isFullHeight}
      $borderRadius={props.borderRadius}
      $allowRowSelect={props.allowRowSelect}
    >
      <TableHead $isStickyHeader={hasStickyHeader}>
        <HeaderRowStyled>
          {props.allowRowSelect && (
            <HeaderCell
              isSortableColumn={false}
              columnName="checkbox"
              width="56px"
              onClick={
                (props.selectionVariant ?? 'checkbox') === 'checkbox'
                  ? props.selectAll
                  : undefined
              }
              borderRadius={props.borderRadius}
              isScrollable={props.isScrollable}
            >
              {(props.selectionVariant ?? 'checkbox') === 'checkbox' ? (
                <Checkbox
                  checked={props.allSelected ?? false}
                  onChange={() => {}}
                />
              ) : null}
            </HeaderCell>
          )}
          {visibleColumns.map((column, columnIndex) => {
            const infoTooltip = props.columnInfoTooltips?.find(
              (c) => c.columnLabel === column.label,
            )?.tooltip;
            const columnWidth = props.columnsWidths?.[columnIndex];
            return (
              <HeaderCell
                isSortableColumn={Boolean(
                  props.sortableColumns?.includes(column.label),
                )}
                isScrollable={props.isScrollable}
                headerText={column.label}
                columnName={column.label}
                width={
                  columnWidth ??
                  (props.allowRowSelect
                    ? `calc((100% - 56px) / ${columns.length})`
                    : `calc(${100 / columns.length}%)`)
                }
                isSortedColumn={
                  props.sortedColumn &&
                  props.sortedColumn.columnName === column.label
                }
                sortDirection={props.sortedColumn?.direction}
                onClick={
                  props.sortableColumns?.includes(column.label)
                    ? props.onColumnSort
                    : undefined
                }
                key={column.label}
                borderRadius={props.borderRadius}
                infoTooltip={infoTooltip}
              />
            );
          })}
        </HeaderRowStyled>
      </TableHead>
      <TableBody>
        {props.tableData.length === 0 &&
          renderEmptyState(props.customEmptyState)}
        {props.tableData.map((data) => (
          <TableRow
            cellData={data.cellData}
            allowRowSelect={props.allowRowSelect}
            selectionVariant={props.selectionVariant ?? 'checkbox'}
            selectionGroupName={props.selectionGroupName}
            rowKey={data.row}
            onClick={(e) => props.onRowClick?.(data.row, data.data, e)}
            isSelected={props.selectedRows?.includes(data.row)}
            data-testid={`${testId}-row-${data.row}`}
            key={uniqueId(`${data.row}-`)}
            fixedRowHeight={props.fixedRowHeight}
            columns={columns}
            visibleColumns={visibleColumns}
            maxColumnWidth={props.maxColumnWidth}
            columnsWidths={props.columnsWidths}
          />
        ))}
      </TableBody>
    </TableStyled>
  );
};
